import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { MEASUREMENT_MODES } from "../../utils/measurementQuestionDetect";

const MeasurementToolsContext = createContext(null);
const AnnotateContext = createContext(null);

export function MeasurementToolsProvider({
  measurementMode,
  showProtractor: controlledProtractor,
  showRuler: controlledRuler,
  onToggleProtractor,
  onToggleRuler,
  children,
}) {
  const controlled =
    controlledProtractor !== undefined &&
    controlledRuler !== undefined &&
    typeof onToggleProtractor === "function" &&
    typeof onToggleRuler === "function";

  const [internalProtractor, setInternalProtractor] = useState(false);
  const [internalRuler, setInternalRuler] = useState(false);

  useEffect(() => {
    if (controlled) return;
    if (measurementMode === MEASUREMENT_MODES.ANGLE) {
      setInternalProtractor(true);
      setInternalRuler(false);
    } else if (measurementMode === MEASUREMENT_MODES.LENGTH) {
      setInternalRuler(true);
      setInternalProtractor(false);
    } else if (measurementMode === MEASUREMENT_MODES.CONSTRUCT) {
      setInternalRuler(true);
      setInternalProtractor(true);
    } else {
      setInternalProtractor(false);
      setInternalRuler(false);
    }
  }, [measurementMode, controlled]);

  const showProtractor = controlled ? controlledProtractor : internalProtractor;
  const showRuler = controlled ? controlledRuler : internalRuler;

  const value = useMemo(
    () => ({
      measurementMode,
      showProtractor,
      setShowProtractor: controlled ? undefined : setInternalProtractor,
      showRuler,
      setShowRuler: controlled ? undefined : setInternalRuler,
      toggleProtractor: controlled ? onToggleProtractor : () => setInternalProtractor((v) => !v),
      toggleRuler: controlled ? onToggleRuler : () => setInternalRuler((v) => !v),
    }),
    [measurementMode, showProtractor, showRuler, controlled, onToggleProtractor, onToggleRuler]
  );

  if (!measurementMode && !controlled) {
    return children;
  }

  return (
    <MeasurementToolsContext.Provider value={value}>
      {children}
    </MeasurementToolsContext.Provider>
  );
}

export function useMeasurementTools() {
  return useContext(MeasurementToolsContext);
}

// The scale of the figure AS RENDERED, reported upward by MeasurementWorkspace so the viewport-level
// ruler/compass overlays can match it. A context rather than props because the workspaces sit several
// components below the take page that owns the overlays.
const FigureScaleContext = createContext(null);

export function FigureScaleProvider({ onEffectivePxPerCm, children }) {
  return (
    <FigureScaleContext.Provider value={onEffectivePxPerCm}>
      {children}
    </FigureScaleContext.Provider>
  );
}

export function useReportFigureScale() {
  return useContext(FigureScaleContext);
}

export function AnnotateProvider({ annotating, onToggle, children }) {
  const value = useMemo(
    () => ({ annotating: Boolean(annotating), toggleAnnotate: onToggle }),
    [annotating, onToggle]
  );
  return <AnnotateContext.Provider value={value}>{children}</AnnotateContext.Provider>;
}

export function useAnnotate() {
  return useContext(AnnotateContext);
}
